import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const SID_KEY = "fc_anon_sid";
const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

/**
 * Generate-or-fetch the anonymous session id we send with every page-view.
 * No PII — just a random localStorage uuid so the backend can compute
 * "unique visitors" without cookies.
 */
function getSid() {
  try {
    let sid = localStorage.getItem(SID_KEY);
    if (!sid) {
      sid = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch {
    return "anon";
  }
}

/**
 * Fires a page-view event on every route change. Skips:
 *   - /admin/* paths (admin shouldn't pollute their own dashboard)
 *   - duplicate fires for the same pathname within a 1500ms window
 *     (React StrictMode + double-effect dev quirks)
 * The backend additionally filters bot user-agents on ingest.
 */
export default function usePageViewTracker() {
  const { pathname, search } = useLocation();
  const lastSent = useRef({ path: "", at: 0 });

  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    const now = Date.now();
    const fullPath = pathname + (search || "");
    if (lastSent.current.path === fullPath && now - lastSent.current.at < 1500) {
      return;
    }
    lastSent.current = { path: fullPath, at: now };

    // Fire-and-forget. We deliberately don't await or surface errors —
    // analytics must never block a page load or show a UI error.
    axios.post(`${API}/analytics/pageview`, {
      path: fullPath.slice(0, 200),
      referrer: document.referrer || "",
      sid: getSid(),
      screen_w: window.innerWidth || null,
    }).catch(() => {});
  }, [pathname, search]);
}
